import classNames from 'classnames/bind';
import { eventNames } from 'constants/eventNames';
import { TFunction } from 'next-i18next';
import { useRouter } from 'next/router';
import { PostBody as PostBodyType, Post as PostType } from 'types/post';

import React, { forwardRef, useEffect } from 'react';

import ReactionSummaryDisplay from 'components/new/Posts/ReactionSummary';
import { useReactionHandler } from 'components/new/Posts/hooks/useReactionHandler';

import { Attachment } from 'schema/Post/types';

import styles from '../Post.module.scss';
import ExpandableContent from '../components/ExpandableContent';
import PostFooter from '../components/PostFooter';

const cx = classNames.bind(styles);
interface PostBodyProps {
  post: PostType;
  postReactionCount: number;
  t: TFunction;
  totalCommentCount: number;
  isReplyPanel?: boolean;
  className?: string;
  footerClassName?: string;
}

const PostBody = forwardRef(
  (
    {
      totalCommentCount,
      postReactionCount,
      isReplyPanel = false,
      post,
      t,
      className,
      footerClassName,
    }: PostBodyProps,
    ref
  ) => {
    const router = useRouter();

    // Manage reactions state at the Post level
    const { reactionStats, myReactions, handleReactionClick } = useReactionHandler({
      id: post.id,
      initialReactionStats: post?.reactionStats || undefined,
      initialMyReactions: post?.myReactions || undefined,
    });

    const commentId = router && router.query && (router.query.comment as string);

    useEffect(() => {
      if (commentId) {
        openReplyPanel();
      }
    }, [commentId]);

    const openReplyPanel = () => {
      PubSub.publish(eventNames.OPEN_REPLY_PANEL, {
        lastElementRef: ref,
        post,
        commentIdFromUrl: commentId,
      });
    };

    const hasAttachment = post.postAttachments && post.postAttachments[0];

    return (
      <div className={cx('post__body', { 'is-reply-panel': isReplyPanel }, className)}>
        <ExpandableContent
          attachments={(post.postAttachments as Attachment[]) || []}
          body={post.body as PostBodyType}
          hasAttachment={!!hasAttachment}
          id={post.id}
          t={t}
        />

        <div className={cx('post__reaction-summary', { 'is-reply-panel': isReplyPanel })}>
          <ReactionSummaryDisplay
            myReactions={myReactions}
            reactionStats={reactionStats}
            onReactionClick={handleReactionClick}
          />
        </div>

        <PostFooter
          className={footerClassName}
          hasReacted={post.hasReacted}
          isReplyPanel={isReplyPanel}
          openReplyPanel={openReplyPanel}
          post={post}
          postReactionCount={postReactionCount}
          totalCommentCount={totalCommentCount}
          onReactionClick={handleReactionClick}
        />
      </div>
    );
  }
);

PostBody.displayName = 'PostBody';
export default PostBody;
